/**
 * Resident "Records Request" intake (records-request.md — Open Records / KORA, MVP tier: log →
 * track → respond → audit). A variant of the concern IntakePage / AskPage: records-sought textarea,
 * optional date range, preferred-format radios, optional record-type picker, optional contact,
 * optional attachments, CAPTCHA. STATE-CONFIGURABLE: the "we respond within N business days under
 * <citationLabel>" note reads its numbers from the tenant's embed config (ThemeProvider's `kora`
 * block) — never hardcoded law. Same anonymous-allowed, WCAG 2.1 AA, error summary + tracking-code
 * confirmation flow as the concern/question intake (♻️ engine).
 */
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitKORA, ApiError } from "../api/index.js";
import type { RecordsPreferredFormat } from "../api/types.js";
import { ContactSection, type ContactValue } from "../components/ContactSection.js";
import { AttachmentUploader, type AttachmentTile } from "../components/AttachmentUploader.js";
import { CaptchaField } from "../components/CaptchaField.js";
import { useCaptcha } from "../components/useCaptcha.js";
import { Field } from "../components/Field.js";
import { Button } from "../components/Button.js";
import { ErrorSummary, type FieldErrorRef } from "../components/ErrorSummary.js";
import { SpinnerIcon } from "../components/icons.js";
import { useDocumentTitle, pageTitle } from "../components/useDocumentTitle.js";
import { useEmbedClose } from "../components/useEmbedClose.js";
import { TownHeader } from "../components/TownHeader.js";
import { ResidentThemeRoot } from "../components/ResidentThemeRoot.js";
import { useTheme } from "../theme/ThemeProvider.js";

const MAX_RECORDS_SOUGHT_LEN = 2000;

const FORMAT_OPTIONS: Array<{ value: RecordsPreferredFormat; label: string }> = [
  { value: "electronic", label: "Electronic (email / download)" },
  { value: "copies", label: "Paper copies (mailed or picked up)" },
  { value: "inspect", label: "Inspect in person" },
];

const ID = {
  recordsSought: "field-kora-records-sought",
  recordType: "field-kora-record-type",
  dateFrom: "field-kora-date-from",
  dateTo: "field-kora-date-to",
  format: "field-kora-format",
  email: "field-kora-contact-email",
  captcha: "field-kora-captcha",
};

interface RecordsRequestPageProps {
  embed?: boolean;
}

export function RecordsRequestPage({ embed = false }: RecordsRequestPageProps): JSX.Element {
  const navigate = useNavigate();
  const { config, koraRecordTypes: recordTypes, kora } = useTheme();
  useDocumentTitle(pageTitle("Open Records Request", config.townName));
  useEmbedClose(embed);

  const [recordsSought, setRecordsSought] = useState("");
  const [recordType, setRecordType] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [preferredFormat, setPreferredFormat] = useState<RecordsPreferredFormat>("electronic");
  const [tiles, setTiles] = useState<AttachmentTile[]>([]);
  const [contact, setContact] = useState<ContactValue>({ name: "", email: "", phone: "" });
  const captcha = useCaptcha();

  const [summaryErrors, setSummaryErrors] = useState<FieldErrorRef[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "error">("idle");
  const [submitErrorMsg, setSubmitErrorMsg] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const requiredMissing = !recordsSought.trim() || !captcha.passed;

  const helperForMissing = useMemo(() => {
    const missing: string[] = [];
    if (!recordsSought.trim()) missing.push("the records you're seeking");
    if (!captcha.passed) missing.push("the spam check");
    return missing;
  }, [recordsSought, captcha.passed]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitState === "submitting") return;

    const nextField: Record<string, string> = {};
    const summary: FieldErrorRef[] = [];
    if (!recordsSought.trim()) {
      nextField[ID.recordsSought] = "Describe the records you're seeking.";
      summary.push({ targetId: ID.recordsSought, message: "Describe the records you're seeking." });
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      nextField[ID.dateTo] = "End date must be on or after the start date.";
      summary.push({ targetId: ID.dateTo, message: "End date must be on or after the start date." });
    }
    const email = contact.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextField[ID.email] = "Enter a valid email, or leave contact blank.";
      summary.push({ targetId: ID.email, message: "Enter a valid email, or leave contact blank." });
    }
    if (!captcha.passed) {
      nextField[ID.captcha] = "Please complete the spam check.";
      summary.push({ targetId: ID.captcha, message: "Please complete the spam check." });
    }
    if (Object.keys(nextField).length) {
      setFieldErrors(nextField);
      setSummaryErrors(summary);
      return;
    }
    setFieldErrors({});
    setSummaryErrors([]);

    // Submit safety net: reuse the fresh inline-solved token (fast no-op) or re-challenge if expired.
    if (!(await captcha.ensureForSubmit())) {
      const msg = "We couldn't verify the spam check. Please try the puzzle again.";
      setSummaryErrors([{ targetId: ID.captcha, message: msg }]);
      return;
    }

    setSubmitState("submitting");
    setSubmitErrorMsg(null);
    try {
      const trimmedContact = {
        name: contact.name.trim() || undefined,
        email: contact.email.trim() || undefined,
        phone: contact.phone.trim() || undefined,
      };
      const hasContact = trimmedContact.name || trimmedContact.email || trimmedContact.phone;
      const result = await submitKORA({
        recordsSought: recordsSought.trim(),
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        preferredFormat,
        recordType: recordType ?? undefined,
        ...(hasContact ? { requesterContact: trimmedContact } : {}),
        attachments: tiles.filter((t) => t.status === "done" && t.key).map((t) => t.key as string),
      });
      const recordTypeLabel = recordTypes.find((t) => t.id === recordType)?.label ?? recordType ?? undefined;
      // Preserve `?embed=1&chrome=off` (and any other query params) across the navigate — App.tsx
      // derives `embed`/`chromeless` fresh from the URL on every route, so an embed submit that
      // dropped the search string here would silently lose chromeless/resize on /confirmation.
      navigate(`/confirmation${window.location.search}`, {
        state: {
          trackingCode: result.trackingCode,
          trackingUrl: result.trackingUrl,
          hasContact: Boolean(hasContact),
          email: trimmedContact.email,
          category: recordTypeLabel,
          isKORA: true,
          koraRespondDays: kora.respondDays,
          koraCitationLabel: kora.citationLabel,
        },
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Something went wrong submitting your records request. Please try again.";
      setSubmitState("error");
      setSubmitErrorMsg(message);
    }
  };

  const submitLabel = submitState === "submitting" ? "Submitting…" : "Submit request";

  return (
    <ResidentThemeRoot className={`td-intake${embed ? " td-intake--embed" : ""}`}>
      <a href="#td-kora-form" className="td-skip-link">
        Skip to the records request form
      </a>
      <TownHeader
        embed={embed}
        subcopy={`Request public records from ${config.townName}. We'll respond within ${kora.respondDays} business days under the ${kora.citationLabel}.`}
      />

      <main className="td-intake__main">
        <h1 className="td-intake__title">Request Public Records</h1>

        <div className="td-alert td-alert--info" role="note">
          <p>
            We'll respond within <strong>{kora.respondDays} business days</strong> under the{" "}
            {kora.citationLabel}.
          </p>
        </div>

        <ErrorSummary errors={summaryErrors} />

        {submitState === "error" && submitErrorMsg ? (
          <div className="td-alert td-alert--danger" role="alert">
            <p>{submitErrorMsg}</p>
            <Button variant="secondary" onClick={() => formRef.current?.requestSubmit()}>
              Try again
            </Button>
          </div>
        ) : null}

        <form
          id="td-kora-form"
          ref={formRef}
          className="td-form"
          noValidate
          onSubmit={onSubmit}
          aria-busy={submitState === "submitting"}
        >
          <Field
            id={ID.recordsSought}
            label="What records are you seeking?"
            required
            helper="Be as specific as you can — dates, names, department, and document type all help staff find the right records."
            error={fieldErrors[ID.recordsSought]}
          >
            {(aria) => (
              <>
                <textarea
                  {...aria}
                  className="td-input td-textarea"
                  rows={4}
                  maxLength={MAX_RECORDS_SOUGHT_LEN}
                  value={recordsSought}
                  onChange={(e) => setRecordsSought(e.target.value)}
                  placeholder="e.g. All incident reports for the corner of 4th and Main from the last 6 months."
                />
                {recordsSought.length > 400 ? (
                  <span className="td-charcount" aria-live="polite">
                    {recordsSought.length} / {MAX_RECORDS_SOUGHT_LEN}
                  </span>
                ) : null}
              </>
            )}
          </Field>

          {recordTypes.length ? (
            <Field id={ID.recordType} label="Record type" optionalHint>
              {(aria) => (
                <select
                  {...aria}
                  className="td-select"
                  value={recordType ?? ""}
                  onChange={(e) => setRecordType(e.target.value || null)}
                >
                  <option value="">Not sure / other</option>
                  {recordTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ) : null}

          <fieldset className="td-fieldset">
            <legend className="td-fieldset__legend">
              Date range <span className="td-field__optional">(optional)</span>
            </legend>
            <p className="td-field__helper" id="td-kora-daterange-helper">
              Narrowing the date range helps staff find the records faster.
            </p>
            <Field id={ID.dateFrom} label="From" optionalHint>
              {(aria) => (
                <input
                  {...aria}
                  className="td-input"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              )}
            </Field>
            <Field id={ID.dateTo} label="To" optionalHint error={fieldErrors[ID.dateTo]}>
              {(aria) => (
                <input
                  {...aria}
                  className="td-input"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              )}
            </Field>
          </fieldset>

          <Field id={ID.format} label="Preferred format" required>
            {(aria) => (
              <select
                {...aria}
                className="td-select"
                value={preferredFormat}
                onChange={(e) => setPreferredFormat(e.target.value as RecordsPreferredFormat)}
              >
                {FORMAT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <AttachmentUploader tiles={tiles} onChange={setTiles} />

          <ContactSection value={contact} onChange={setContact} emailError={fieldErrors[ID.email]} />

          <CaptchaField
            ref={captcha.containerRef}
            id={ID.captcha}
            status={captcha.status}
            error={captcha.error ?? undefined}
            passed={captcha.passed}
            onChange={(v) => {
              captcha.onChange(v);
              if (v && fieldErrors[ID.captcha]) {
                setFieldErrors((f) => {
                  const { [ID.captcha]: _drop, ...rest } = f;
                  return rest;
                });
              }
            }}
            onRetry={captcha.retry}
          />

          <div className="td-submit">
            <Button
              type="submit"
              fullWidth
              disabled={submitState === "submitting"}
              aria-disabled={requiredMissing || submitState === "submitting"}
            >
              {submitState === "submitting" ? (
                <>
                  <SpinnerIcon width={18} height={18} />
                  {submitLabel}
                </>
              ) : (
                submitLabel
              )}
            </Button>
            {requiredMissing && submitState !== "submitting" ? (
              <p className="td-submit__hint" aria-live="polite">
                Still needed: {helperForMissing.join(", ")}.
              </p>
            ) : null}
          </div>
        </form>
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
        {config.privacyUrl ? (
          <a href={config.privacyUrl} rel="noopener">
            Privacy
          </a>
        ) : null}
      </footer>
    </ResidentThemeRoot>
  );
}
